<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from "vue";

import type {
  ActionVisibility,
  TableAction,
  TableOpts,
} from "../sbi/protocol";
import { formatValue } from "../format.ts";
import { EMBED_BP, useElementWidth, useMediaQuery } from "../sbi/embed-width.ts";
import { usePrintMode } from "../sbi/print-mode.ts";
import LucentIcon from "./LucentIcon.vue";
import LucentSpinner from "./LucentSpinner.vue";
import { formatError } from "./errorText.ts";


const props = withDefaults(
  defineProps<{
    rows: unknown[];
    opts?: TableOpts<unknown>;
    loading?: boolean;
    /**
     * Set when the marker bound to this table rejected. Renders
     * `opts.error` (or the generic fallback) in place of the table.
     * Mutually exclusive with `loading`.
     */
    errored?: boolean;
    /**
     * Error correlation tag minted at the failure site (server or
     * worker). Appended to the user-facing error banner as
     * `[EX:<id>]`. See `errorText.ts` / `docs/plans/error-correlation-ids.md`.
     */
    exId?: string;
    /**
     * Field → format registry from `sbi.format(...)`. Each column's
     * `key` is looked up here when its own `format` isn't supplied,
     * letting authors declare a metric's format once and have it
     * apply to every table cell drawn from that field.
     */
    formats?: Record<string, string>;
    /**
     * Per-row visibility for `opts.actions`, worker-evaluated from
     * each action's `show` predicate. Keys are row indices as strings
     * (matches the worker's JSON-safe key shape). Absent when no
     * action declares `show` — the component infers "every action
     * enabled on every row" in that case.
     */
    visibleActionsById?: Record<string, ActionVisibility[]>;
  }>(),
  {
    formats: () => ({}),
  },
);

const emit = defineEmits<{
  (e: "click-target", id: string, rowIndex: number): void;
}>();

interface Column {
  key: string;
  header: string;
  format?: string;
  align: "left" | "right" | "center";
  sortable: boolean;
  /** Cell text may wrap (capped at 40ch) instead of forcing scroll. */
  wrap: boolean;
}

// Auto-wrap policy for columns that don't set `wrap`. Only long free
// text wraps — descriptions, emails, addresses. Numbers, dates, codes
// and short labels stay on one line so "12 Mar 2024" never breaks.
const AUTO_WRAP_MIN_CHARS = 24;
const AUTO_WRAP_SAMPLE_ROWS = 50;

function autoWrap(key: string, align: Column["align"], fmt?: string): boolean {
  if (align !== "left") return false;
  const rows = props.rows as Record<string, unknown>[];
  const sample = rows.slice(0, AUTO_WRAP_SAMPLE_ROWS);
  const first = sample.find((r) => r?.[key] !== null && r?.[key] !== undefined)?.[key];
  if (typeof first === "number") return false;
  let longest = 0;
  for (const r of sample) longest = Math.max(longest, cell(r?.[key], fmt).length);
  return longest > AUTO_WRAP_MIN_CHARS;
}

// Per-column format resolution order:
//   1. explicit `c.format` on the column override (most local)
//   2. per-call default `opts.format` (DefaultOpts) — applies when the
//      author wants the same format across every cell of a small table
//   3. registered format for the column's `key` via `sbi.format(...)`
// Last fallback is no-format — cells render the raw value.
//
// The fallbacks also apply to inferred columns (no `opts.columns`),
// so a registered format for e.g. "revenue" formats the column even
// when the author left the table un-configured.
const callDefaultFormat = computed(() => props.opts?.format);

const inferredColumns = computed<Column[]>(() => {
  // Tree mode strips client-side sort across the board — even if the
  // author opts a column in, the header is rendered non-interactive.
  // See the TableOpts.tree docblock for the rationale (sort interacts
  // awkwardly with hierarchy in v1).
  const allowSortable = !isTree.value;
  // The tree label column wraps by default: truncating a node name
  // hides data, and a deep indent eats the line on phones.
  const treeLabel = treeCfg.value?.label;
  if (props.opts?.columns?.length) {
    return props.opts.columns.map((c) => {
      const format = c.format ?? callDefaultFormat.value ?? props.formats[c.key];
      const align =
        c.align ?? guessAlign((props.rows[0] as Record<string, unknown> | undefined)?.[c.key]);
      return {
        key: c.key,
        header: c.header ?? c.key,
        format,
        align,
        sortable: allowSortable && c.sortable === true,
        wrap: c.wrap ?? (c.key === treeLabel || autoWrap(c.key, align, format)),
      };
    });
  }
  const first = props.rows[0] as Record<string, unknown> | undefined;
  if (!first) return [];
  return Object.keys(first).map((key) => {
    const format = callDefaultFormat.value ?? props.formats[key];
    const align = guessAlign(first[key]);
    return {
      key,
      header: key,
      format,
      align,
      sortable: false,
      wrap: key === treeLabel || autoWrap(key, align, format),
    };
  });
});


// Tree mode is opt-in via `opts.tree`. Presence of the field is the
// switch: when set, the table pivots `rows` into a hierarchy and
// renders the indented-list layout; otherwise the flat path is
// unchanged. Sort and the "+ N more" cap are deliberately disabled
// in tree mode (see TableOpts.tree docblock for the rationale).
const treeCfg = computed(() => props.opts?.tree);
const isTree = computed(() => !!treeCfg.value);

const maxRows = computed(() => props.opts?.maxRows ?? 20);

// Click-to-reveal pagination state. Starts at 1 (one page = maxRows
// rows shown). Each click on the "+ N more" overflow row bumps it,
// extending the visible window by another `maxRows`. Local to the
// component — no author API, no protocol field, no persistence
// beyond the component instance lifetime.
//
// State is tied to the Vue component instance, not to `props.rows`.
// A state-driven rerun (filter change, drilldown open/close) reuses
// the same `InsightTable` instance — EmbedHost keys its `InsightNode`
// children positionally — so sort / pagination / expansion all
// survive the rerun. Reset happens naturally when the table is
// unmounted (kind change, conditional removes it from the tree).
// Earlier this was reset on every `props.rows` reference change,
// which incidentally clobbered state every time a drilldown opened
// because the parent insight reruns even when its data is unchanged.
const visiblePages = ref(1);

// Client-side sort state. Persists across reruns alongside
// `visiblePages` — see the rationale above.
//
// `sortKey === null` is the unsorted state (the third arm of the
// asc → desc → unsorted cycle). In that state we render rows in
// their original order — same as a table with no sort at all.
const sortKey = ref<string | null>(null);
const sortDir = ref<"asc" | "desc">("asc");

// Sorted projection carries the row alongside its index in the
// *original* `props.rows` array. The original index is what row-click
// dispatch in the worker uses (see `tableRowsById` in worker-runtime),
// so emitting the visible index after a sort would resolve to the
// wrong row in `onRowClick(row, rowIndex)`. Keeping the pair here lets
// `onRowClick` translate visible → original before emitting.
interface SortedRow {
  row: Record<string, unknown>;
  originalIndex: number;
}

const sortedRows = computed<SortedRow[]>(() => {
  const rows = props.rows as Record<string, unknown>[];
  const paired: SortedRow[] = rows.map((row, originalIndex) => ({
    row,
    originalIndex,
  }));
  const key = sortKey.value;
  if (key === null) return paired;
  const dir = sortDir.value === "asc" ? 1 : -1;
  // Stable sort: tie-break by original index so equal values keep
  // their relative order across asc/desc toggles. `Array.prototype.sort`
  // is spec-stable since ES2019; the tie-break belt-and-braces it.
  return paired.sort((a, b) => {
    const cmp = compareValues(a.row[key], b.row[key]);
    return cmp !== 0 ? cmp * dir : a.originalIndex - b.originalIndex;
  });
});

const visibleRows = computed(() =>
  sortedRows.value.slice(0, maxRows.value * visiblePages.value),
);

const overflow = computed(() =>
  Math.max(0, props.rows.length - maxRows.value * visiblePages.value),
);

// ─── Tree layout — pivot + expansion state ──────────────────────────────────
//
// Tree mode reuses the row shape authors already write for a flat
// table: one row per node, columns hold per-node metadata. The
// pivot below only consumes the three columns the author nominated
// in `opts.tree` (`id`, `parent`, `label`) to build the hierarchy;
// every other column is rendered identically to the flat path.
//
// Output is a depth-tagged *flat* list of `TreeRow` entries — the
// template iterates it as a single `<tr v-for>`, the same way it
// iterates `visibleRows` in flat mode. This keeps the rendering
// path identical (one row per `<tr>`, columns from
// `inferredColumns`) and lets the label column add an indent + caret
// without forking the whole table layout.

interface TreeRow {
  /** Original row from `props.rows`. */
  row: Record<string, unknown>;
  /** Index in the *original* `props.rows` array — used by `onRowClick`. */
  originalIndex: number;
  /** Node id, string-coerced. Stable key for expansion state. */
  nodeId: string;
  /** 0 for roots, 1 for direct children of roots, ... */
  depth: number;
  /** Does this node have at least one child? Drives caret visibility. */
  hasChildren: boolean;
}

// String-coerce ids so numeric/string parent columns interoperate
// (DuckDB can hand back either depending on the source column). An
// `id` of `null`/`undefined` becomes the empty string and gets
// treated as a root (its `parent` lookup misses).
function coerceId(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

// Built once per `props.rows` change. Resolves the full ordered list
// of `{ node, depth, hasChildren }` triples by walking children of
// each root in their original order. Rows whose `parent` matches no
// other row's `id` are surfaced as roots (rather than silently
// dropped) so a partial result set still renders something.
const treeNodes = computed<TreeRow[]>(() => {
  const cfg = treeCfg.value;
  if (!cfg) return [];
  const rows = props.rows as Record<string, unknown>[];

  type Bucket = { row: Record<string, unknown>; originalIndex: number; id: string };
  const buckets: Bucket[] = rows.map((row, originalIndex) => ({
    row,
    originalIndex,
    id: coerceId(row[cfg.id]),
  }));

  const idSet = new Set<string>();
  for (const b of buckets) if (b.id) idSet.add(b.id);

  // Group children under their parent id; roots collected separately
  // so a row whose `parent` references a missing id still renders.
  const childrenByParent = new Map<string, Bucket[]>();
  const roots: Bucket[] = [];
  for (const b of buckets) {
    const parentId = coerceId(b.row[cfg.parent]);
    if (parentId && idSet.has(parentId)) {
      const list = childrenByParent.get(parentId) ?? [];
      list.push(b);
      childrenByParent.set(parentId, list);
    } else {
      roots.push(b);
    }
  }

  const out: TreeRow[] = [];
  const visit = (b: Bucket, depth: number): void => {
    const kids = childrenByParent.get(b.id) ?? [];
    out.push({
      row: b.row,
      originalIndex: b.originalIndex,
      nodeId: b.id || `__orphan_${b.originalIndex}`,
      depth,
      hasChildren: kids.length > 0,
    });
    for (const child of kids) visit(child, depth + 1);
  };
  for (const r of roots) visit(r, 0);
  return out;
});

// Expansion state. Holds the *expanded* node ids; nodes absent from
// the set are collapsed. Modelling expansion (not collapse) means
// the initial state — set from `initialDepth` — populates the set
// once and the template just checks membership.
//
// Like sort/pagination, expansion is tied to the component instance
// lifetime, not to `props.rows`. Seeded once on mount from
// `initialDepth`, re-seeded only if `treeCfg` itself changes (a
// different `id`/`parent`/`label` triple genuinely means a different
// hierarchy and the old node ids no longer mean anything). A rerun
// that swaps in a new row reference keeps the existing expansion —
// drilldown open/close was clobbering it previously.
const expanded = ref<Set<string>>(new Set());

function seedExpansion(): void {
  const cfg = treeCfg.value;
  if (!cfg) {
    expanded.value = new Set();
    return;
  }
  const depth = cfg.initialDepth ?? 1;
  const next = new Set<string>();
  for (const n of treeNodes.value) {
    if (n.hasChildren && n.depth < depth) next.add(n.nodeId);
  }
  expanded.value = next;
}

seedExpansion();
watch(treeCfg, () => {
  seedExpansion();
});

// Walk the depth-tagged list and skip any node whose ancestor chain
// has a collapsed link. Tracks the depth of the deepest collapsed
// ancestor we're currently under; rows shallower than that depth
// pop us back out.
const visibleTreeRows = computed<TreeRow[]>(() => {
  if (!isTree.value) return [];
  const out: TreeRow[] = [];
  let hiddenBelowDepth = Infinity;
  for (const n of treeNodes.value) {
    if (n.depth <= hiddenBelowDepth) hiddenBelowDepth = Infinity;
    if (n.depth > hiddenBelowDepth) continue;
    out.push(n);
    if (n.hasChildren && !expanded.value.has(n.nodeId)) {
      hiddenBelowDepth = n.depth;
    }
  }
  return out;
});

function toggleExpansion(nodeId: string): void {
  const next = new Set(expanded.value);
  if (next.has(nodeId)) next.delete(nodeId);
  else next.add(nodeId);
  expanded.value = next;
}

function onCaretKey(event: KeyboardEvent, nodeId: string): void {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    toggleExpansion(nodeId);
  }
}

// The rows the single `<tbody>` paints: the sorted, revealed slice in
// flat mode, the expanded tree walk in tree mode. Tree entries carry
// `nodeId` / `depth` / `hasChildren` for the label cell's indent and
// caret; everything else (cells, trailing cell, expander row) is
// shared.
type BodyRow = SortedRow & Partial<Omit<TreeRow, "row" | "originalIndex">>;
const bodyRows = computed<BodyRow[]>(() =>
  isTree.value ? visibleTreeRows.value : visibleRows.value,
);

// Horizontal-scroll shadow affordance. The pure-CSS
// `background-attachment` trick this replaced couldn't tell "content
// fits, no scrollbar" from "scrolled hard to one edge", so it painted
// edge shadows on tables that didn't overflow at all. Driving the
// affordance from the scroller's real geometry lets us show a shadow
// only on a side that actually has offstage content.
const scroller = ref<HTMLElement | null>(null);
const tableEl = ref<HTMLTableElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

// ─── Width-driven layout ───────────────────────────────────────────
//
// Narrow behaviour (collapsed columns, one sticky trailing cell, the
// action sheet) keys off the table's OWN width — measured on
// `.table-wrap`, a scroll container, so the table's content can't push
// that width back up — never the viewport. A table in a 350px column
// on a desktop gets the narrow layout; a full-width table on a tablet
// doesn't.
//
// Driven by script-set classes rather than `@container` rules because
// print must keep the desktop layout: a two-column PDF cell is ~330px,
// which a CSS container query would read as narrow. `isPrint` forces
// every flag off. A width of 0 means "not measured yet" (happy-dom,
// first frame) and also renders desktop.
const isPrint = usePrintMode();
const wrapWidth = useElementWidth(scroller);
const isNarrow = computed(
  () => !isPrint() && wrapWidth.value > 0 && wrapWidth.value < EMBED_BP.compact,
);
const isCompact = computed(
  () => !isPrint() && wrapWidth.value > 0 && wrapWidth.value < EMBED_BP.narrow,
);
const coarsePointer = useMediaQuery("(pointer: coarse)");
const isCoarse = computed(() => !isPrint() && coarsePointer.value);

// `scrollLeft`/`scrollWidth`/`clientWidth` are fractional under
// browser zoom and HiDPI, so compare against a 1px slack on each edge
// rather than `> 0` / `=== max` to avoid a shadow that flickers on at
// the extremes.
function updateScrollShadows(): void {
  const el = scroller.value;
  if (!el) {
    canScrollLeft.value = false;
    canScrollRight.value = false;
    return;
  }
  const maxScroll = el.scrollWidth - el.clientWidth;
  canScrollLeft.value = el.scrollLeft > 1;
  canScrollRight.value = el.scrollLeft < maxScroll - 1;
  evaluateAutoCollapse();
}

let resizeObserver: ResizeObserver | null = null;

// Container width and content width both feed overflow. The scroller's
// width — re-layout of the card (responsive column, drilldown modal) —
// already has an observer in `useElementWidth`, so follow `wrapWidth`
// rather than observing `.table-wrap` a second time; that also keeps the
// narrow flags and the overflow check reading the same frame's width.
watch(wrapWidth, () => updateScrollShadows());

onMounted(() => {
  updateScrollShadows();
  if (typeof ResizeObserver !== "undefined") {
    // The <table> covers content-width changes that don't resize the
    // scroller (web fonts landing, columns collapsing).
    resizeObserver = new ResizeObserver(() => updateScrollShadows());
    if (tableEl.value) resizeObserver.observe(tableEl.value);
  }
});

// The <table> mounts after the loading placeholder, so observe it
// whenever it (re)appears.
watch(tableEl, (el, prev) => {
  if (prev) resizeObserver?.unobserve(prev);
  if (el) resizeObserver?.observe(el);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});

// Sort, reveal-more, and new data all change the rendered row set —
// and thus the content width — without firing scroll or resize. Wait
// for the DOM to flush before re-measuring.
watch(visibleRows, () => {
  void nextTick(updateScrollShadows);
});

// Header click handler. Three-state cycle:
//   unsorted        → asc on the clicked column
//   asc on column   → desc
//   desc on column  → unsorted (clear)
//   asc/desc on a different column → asc on the new column
//
// Clicking a non-sortable column header is a no-op (the template
// also guards via `tabindex` / cursor, but the JS guard is the
// source of truth since the column list comes from author opts).
function onHeaderActivate(col: Column): void {
  if (!col.sortable) return;
  visiblePages.value = 1;
  if (sortKey.value !== col.key) {
    sortKey.value = col.key;
    sortDir.value = "asc";
    return;
  }
  if (sortDir.value === "asc") {
    sortDir.value = "desc";
    return;
  }
  sortKey.value = null;
  sortDir.value = "asc";
}

function onHeaderKey(event: KeyboardEvent, col: Column): void {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    onHeaderActivate(col);
  }
}

function ariaSortFor(col: Column): "ascending" | "descending" | "none" | undefined {
  if (!col.sortable) return undefined;
  if (sortKey.value !== col.key) return "none";
  return sortDir.value === "asc" ? "ascending" : "descending";
}

// Comparator for raw cell values. Numbers compare numerically,
// strings lexicographically (locale-aware so e.g. "ä" sorts near
// "a"), null/undefined sort *last* in both directions — the usual
// expectation for table sorts, and the alternative ("nulls first
// when ascending") is rarely what authors want. Mixed-type columns
// fall back to a stable string compare so we never throw.
function compareValues(a: unknown, b: unknown): number {
  const aNull = a === null || a === undefined;
  const bNull = b === null || b === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof a === "number" && typeof b === "number") {
    if (Number.isNaN(a) && Number.isNaN(b)) return 0;
    if (Number.isNaN(a)) return 1;
    if (Number.isNaN(b)) return -1;
    return a - b;
  }
  if (typeof a === "string" && typeof b === "string") {
    return a.localeCompare(b);
  }
  if (typeof a === "boolean" && typeof b === "boolean") {
    return a === b ? 0 : a ? 1 : -1;
  }
  return String(a).localeCompare(String(b));
}

function onRevealMore(): void {
  visiblePages.value += 1;
}

function onRevealMoreKey(event: KeyboardEvent): void {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    onRevealMore();
  }
}

// Empty when the data resolved cleanly but produced zero rows. The
// host falls back to `opts.empty` text (when authors opt in) instead
// of rendering an empty `<table>` shell with just a header.
const isEmpty = computed(
  () => !props.loading && !props.errored && props.rows.length === 0,
);

const emptyText = computed(() => props.opts?.empty);
const errorText = computed(() =>
  formatError(props.opts?.error ?? "Couldn't load this table", props.exId),
);

function guessAlign(sample: unknown): "left" | "right" {
  return typeof sample === "number" ? "right" : "left";
}

function cell(value: unknown, fmt?: string): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "number" || typeof value === "string") {
    return formatValue(value, fmt);
  }
  return String(value);
}

// `visibleIndex` is the row's position in the *sorted* slice (flat
// mode) or the *expanded* tree-row slice (tree mode) the template
// iterates. Either way, translate back to the original `props.rows`
// index before emitting, so the worker's `bindTableClick` resolves
// the correct row out of `tableRowsById` (which always holds the
// original, pre-pivot row array).
function onRowClick(visibleIndex: number) {
  if (!props.opts?.id) return;
  const entry = bodyRows.value[visibleIndex];
  if (!entry) return;
  emit("click-target", props.opts.id, entry.originalIndex);
}

// ─── Per-row action icons ──────────────────────────────────────────
//
// `opts.actions` declares up to 6 icon buttons per row. Each action
// becomes its own click-target — the worker auto-prefixes the wire id
// as `<tableId>:<actionId>` so cross-table collisions are impossible.
// We emit that same prefixed id back to the worker on click.
//
// Visibility per row comes from `visibleActionsById` (worker-computed
// from each action's `show` predicate). When the prop is absent we
// infer "every action enabled on every row" — the common case where
// no `show` predicates were declared.

const tableActions = computed<ReadonlyArray<TableAction<unknown>>>(
  () => props.opts?.actions ?? [],
);
const hasActions = computed(() => tableActions.value.length > 0);

function visibleActionsFor(originalIndex: number): ReadonlyArray<ActionVisibility> {
  const map = props.visibleActionsById;
  if (!map) {
    // No predicates declared — every action visible & enabled.
    return tableActions.value.map((a) => ({ id: a.id, state: "enabled" as const }));
  }
  return map[String(originalIndex)] ?? [];
}

function labelFor(actionId: string): string {
  const a = tableActions.value.find((x) => x.id === actionId);
  return a?.label ?? actionId;
}

function getIcon(actionId: string): string {
  const a = tableActions.value.find((x) => x.id === actionId);
  return a?.icon ?? "info";
}

function tooltipFor(vis: ActionVisibility): string {
  const label = labelFor(vis.id);
  if (vis.state === "disabled" && vis.reason) {
    return `${label} — ${vis.reason}`;
  }
  return label;
}

function onActionClick(actionId: string, originalIndex: number) {
  if (!props.opts?.id) return;
  const internalId = `${props.opts.id}:${actionId}`;
  emit("click-target", internalId, originalIndex);
}

// ─── Narrow layout: action collapse + action sheet ────────────────
//
// When the table is narrow — or on a touch screen with more than one
// action — the per-row icon row collapses into a single `ellipsis`
// button that opens an action sheet listing every visible action with
// its label spelled out. Solves the 28px touch-target problem and the
// no-tooltip-on-touch problem (icon labels are only `title` text).
// A single action on a touch screen stays inline, just bigger.

const collapseActions = computed(
  () =>
    !isPrint() &&
    (isNarrow.value || (isCoarse.value && tableActions.value.length > 1)),
);

// Open action sheet: which row's actions are currently shown.
// `null` = closed. Storing the *original* row index (not the visible
// one) keeps the sheet anchored to the right action set across
// sort/expand transitions of the table beneath it.
const openSheetIndex = ref<number | null>(null);
const sheetRef = ref<HTMLDialogElement | null>(null);
const sheetTitleId = useId();
let sheetTrigger: HTMLElement | null = null;

function openActionSheet(originalIndex: number, event?: Event) {
  sheetTrigger = (event?.currentTarget as HTMLElement | null) ?? null;
  openSheetIndex.value = originalIndex;
}

function closeActionSheet() {
  const d = sheetRef.value;
  if (d?.open && typeof d.close === "function") d.close();
  else onSheetClose();
}

// The sheet is a native <dialog> rendered INSIDE this component — so
// it stays in the shadow root with its scoped styles (a Teleport to
// <body> would leave them behind) — and `showModal()` lifts it into
// the top layer: above `.table-wrap`'s overflow, above any host-page
// transform, and above a drilldown modal it may itself sit inside.
// Esc (`cancel` → `close`) and backdrop clicks are native.
watch(
  openSheetIndex,
  (i) => {
    const d = sheetRef.value;
    if (!d || !d.isConnected) return;
    if (i !== null && !d.open && typeof d.showModal === "function") d.showModal();
    if (i === null && d.open && typeof d.close === "function") d.close();
  },
  { flush: "post" },
);

function onSheetClose() {
  openSheetIndex.value = null;
  if (sheetTrigger?.isConnected) sheetTrigger.focus({ preventScroll: true });
  sheetTrigger = null;
}

// Backdrop clicks land on the <dialog> itself (it has no padding; the
// body wrapper does), so `target === dialog` means "outside".
function onSheetClick(event: MouseEvent) {
  if (event.target === sheetRef.value) closeActionSheet();
}

function onSheetAction(actionId: string, disabled: boolean) {
  if (disabled) return;
  const originalIndex = openSheetIndex.value;
  closeActionSheet();
  if (originalIndex !== null) onActionClick(actionId, originalIndex);
}

// Rotating to a wider layout puts the inline buttons back; don't leave
// a stale sheet open over them.
watch(collapseActions, (collapsed) => {
  if (!collapsed && openSheetIndex.value !== null) closeActionSheet();
});

const sheetActions = computed<ReadonlyArray<ActionVisibility>>(() => {
  if (openSheetIndex.value === null) return [];
  return visibleActionsFor(openSheetIndex.value);
});

// Sheet heading: the open row's first column, so a reader at 320px
// can tell which row the actions apply to.
const sheetRowLabel = computed(() => {
  const i = openSheetIndex.value;
  const first = inferredColumns.value[0];
  if (i === null || !first) return "Row actions";
  const row = (props.rows as Record<string, unknown>[])[i];
  return cell(row?.[first.key], first.format) || "Row actions";
});

// ─── Narrow column collapse — `mobileColumns` + per-row expander ───
//
// When the table is narrow, only the "mobile" columns
// render — every other column moves into a per-row expander opened by
// the chevron in the trailing cell. The visible set is the author's
// `mobileColumns`; without one, a default (first column + first
// numeric column) applies, but only once the table genuinely overflows
// its container. `mobileColumns: false` opts out entirely.
//
// Trees collapse too: the label column (with its caret) always stays,
// and the chevron only appears on rows with collapsed values or
// actions (`rowHasDetails`).
//
// This block lives at the bottom of the script because it depends on
// `isNarrow` / `sortedRows` / `inferredColumns` — declaring
// it earlier would trip a TDZ error on the `watch(sortedRows, …)`
// registration below, since `watch` resolves its source reference at
// registration time rather than lazily like a computed callback.

// Keys that name no column are dropped (`opts` is a passthrough, so a
// typo or a bare string reaches us unchecked); none left falls back to
// the default pair rather than a table with no data columns. A tree
// always keeps its label column — it carries the caret — so it's
// prepended when the author left it out.
const authorMobileColumns = computed<ReadonlyArray<string> | false | null>(() => {
  const list = props.opts?.mobileColumns as unknown;
  if (list === false) return false;
  if (!Array.isArray(list)) return null;
  const known = new Set(inferredColumns.value.map((c) => c.key));
  const keys = list.filter((k): k is string => typeof k === "string" && known.has(k));
  if (keys.length === 0) return null;
  const label = treeCfg.value?.label;
  if (label && known.has(label) && !keys.includes(label)) keys.unshift(label);
  return keys;
});

const autoEligible = computed(
  () =>
    isNarrow.value &&
    authorMobileColumns.value === null &&
    inferredColumns.value.length >= 3,
);

// Default visible set: the first column (usually the row's name) plus
// the first right-aligned column after it (usually its headline
// number), falling back to the second column. A tree leads with its
// label column instead.
const defaultMobileKeys = computed<ReadonlyArray<string>>(() => {
  const cols = inferredColumns.value;
  if (cols.length === 0) return [];
  const lead = cols.find((c) => c.key === treeCfg.value?.label) ?? cols[0];
  const rest = cols.filter((c) => c !== lead);
  const metric = rest.find((c) => c.align === "right") ?? rest[0];
  return metric ? [lead.key, metric.key] : [lead.key];
});

// Latched: collapse when the full table overflows, and only expand
// again once the container is at least as wide as the full table was
// — otherwise collapsing (which removes the overflow) would
// immediately undo itself.
const autoCollapse = ref(false);
let naturalWidth = 0;

function evaluateAutoCollapse(): void {
  const el = scroller.value;
  if (!autoEligible.value || !el) {
    if (autoCollapse.value) autoCollapse.value = false;
    return;
  }
  if (!autoCollapse.value && el.scrollWidth - el.clientWidth > 1) {
    naturalWidth = el.scrollWidth;
    autoCollapse.value = true;
  } else if (autoCollapse.value && el.clientWidth >= naturalWidth) {
    autoCollapse.value = false;
  }
}

// New data or a new column set may fit where the old didn't: render
// the full table, then re-measure (before paint — `nextTick` runs in
// the same task as the DOM patch).
watch(
  [() => props.rows, () => inferredColumns.value.map((c) => c.key).join("\u0000")],
  () => {
    if (!autoCollapse.value) return;
    autoCollapse.value = false;
    void nextTick(updateScrollShadows);
  },
);
watch(autoEligible, () => void nextTick(updateScrollShadows));

const mobileColumnKeys = computed<ReadonlyArray<string> | null>(() => {
  const list = authorMobileColumns.value;
  if (list === false) return null;
  if (list) return list;
  return autoCollapse.value ? defaultMobileKeys.value : null;
});

const isMobileCollapsed = computed(
  () => isNarrow.value && mobileColumnKeys.value !== null,
);

// Columns actually painted in each `<tr>` — the full set on desktop /
// tree / no-collapse path, filtered down to the mobile keys
// (preserving that list's order, not `columns`' order) when narrow.
const visibleColumns = computed<Column[]>(() => {
  const keys = mobileColumnKeys.value;
  if (!isMobileCollapsed.value || !keys) return inferredColumns.value;
  const byKey = new Map(inferredColumns.value.map((c) => [c.key, c]));
  const out: Column[] = [];
  for (const k of keys) {
    const col = byKey.get(k);
    if (col) out.push(col);
  }
  return out;
});

// Columns pushed into the per-row expander. Order preserved from
// `inferredColumns` (author's `columns[]` order) so the Field: Value
// list under each row reads top-down the same way the desktop layout
// reads left-to-right.
const collapsedColumns = computed<Column[]>(() => {
  if (!isMobileCollapsed.value) return [];
  const visibleKeys = new Set(visibleColumns.value.map((c) => c.key));
  return inferredColumns.value.filter((c) => !visibleKeys.has(c.key));
});

const hasExpander = computed(() => collapsedColumns.value.length > 0);

// Whether a row gets the chevron. Flat rows always do — a consistent
// right edge beats hiding it per row. Tree rows only when there is
// something to show: group rows often carry nothing but a label, and
// their caret is already on the left.
function rowHasDetails(originalIndex: number, row: Record<string, unknown>): boolean {
  if (!isTree.value) return true;
  if (hasActions.value && visibleActionsFor(originalIndex).length > 0) return true;
  return collapsedColumns.value.some((c) => cell(row[c.key], c.format) !== "");
}

// Narrow tables get ONE sticky trailing cell instead of an actions
// column plus a chevron column (~120px of a 320px screen). With an
// expander it holds the chevron and the row's actions move into the
// expanded details; otherwise it holds the `ellipsis` sheet trigger.
const useTrailCell = computed(
  () => isNarrow.value && (hasActions.value || hasExpander.value),
);
const trailingCount = computed(() =>
  useTrailCell.value ? 1 : hasActions.value ? 1 : 0,
);
const totalColspan = computed(() => visibleColumns.value.length + trailingCount.value);

// Per-row expander state, keyed by the row's original index (stable
// across sort — `SortedRow.originalIndex` is what the click handler
// resolves against).
const expandedRows = ref<Set<number>>(new Set());

function toggleRowExpansion(originalIndex: number): void {
  const next = new Set(expandedRows.value);
  if (next.has(originalIndex)) next.delete(originalIndex);
  else next.add(originalIndex);
  expandedRows.value = next;
}

// Collapse everything when the row set churns — the ids may still
// exist but the reader almost never wants the same rows to stay open
// through a filter change / sort flip / dataset swap. Cheaper than
// diffing which ids survived. Same when the expander goes away (the
// table widened), so rows don't reappear open on the next narrowing.
const clearExpandedRows = (): void => {
  if (expandedRows.value.size > 0) expandedRows.value = new Set();
};
watch(sortedRows, clearExpandedRows);
watch(hasExpander, (has) => {
  if (!has) clearExpandedRows();
});

// Print tables with many columns get a denser type size so they fit
// the 180mm page instead of being clipped.
const isDensePrint = computed(() => isPrint() && inferredColumns.value.length >= 7);
</script>

<template>
  <div
    ref="scroller"
    class="table-wrap"
    :class="{
      'is-loading': loading,
      'is-errored': errored,
      'is-empty': isEmpty && !!emptyText,
      'can-scroll-left': canScrollLeft,
      'can-scroll-right': canScrollRight,
      'is-narrow': isNarrow,
      'is-compact': isCompact,
      'is-coarse': isCoarse,
      'is-dense': isDensePrint,
    }"
    :style="{ '--lucent-table-viewport': wrapWidth > 0 ? `${wrapWidth}px` : '100%' }"
    @scroll="updateScrollShadows"
  >
    <div v-if="loading" class="table-spinner">
      <LucentSpinner :size="40" />
    </div>
    <div
      v-else-if="errored"
      class="table-placeholder table-placeholder--error"
    >
      {{ errorText }}
    </div>
    <div
      v-else-if="isEmpty && emptyText"
      class="table-placeholder table-placeholder--empty"
    >
      {{ emptyText }}
    </div>
    <template v-else>
    <table ref="tableEl">
      <thead>
        <tr>
          <th
            v-for="col in visibleColumns"
            :key="col.key"
            :class="{
              num: col.align === 'right',
              sortable: col.sortable,
              'is-sorted': sortKey === col.key,
              wrap: col.wrap,
            }"
            :style="{ textAlign: col.align }"
            :aria-sort="ariaSortFor(col)"
            :tabindex="col.sortable ? 0 : undefined"
            :role="col.sortable ? 'button' : undefined"
            data-part="table-header"
            @click="onHeaderActivate(col)"
            @keydown="onHeaderKey($event, col)"
          >
            <!--
              Inner inline-flex container holds the label + sort
              indicator. The flex container lives on this span, not
              the `<th>` itself — applying `display: flex` to a
              table-cell strips its `display: table-cell` behavior
              and the column collapses out of the table layout,
              which manifested as headers stacking vertically in
              narrow contexts (e.g. the drilldown modal).
            -->
            <span class="th-inner">
              <span class="th-label">{{ col.header }}</span>
              <span
                v-if="col.sortable"
                class="sort-indicator"
                :class="{
                  'is-asc': sortKey === col.key && sortDir === 'asc',
                  'is-desc': sortKey === col.key && sortDir === 'desc',
                }"
                aria-hidden="true"
              >
                {{ sortKey === col.key ? (sortDir === "asc" ? "▲" : "▼") : "↕" }}
              </span>
            </span>
          </th>
          <!--
            Trailing column header. Narrow tables get ONE sticky cell
            (chevron or row-actions trigger); wider tables keep the
            sticky actions column. Empty label — icons are
            self-describing via per-button labels / the sheet — but the
            slot keeps data cells from bleeding under the buttons on
            horizontal scroll. aria-label gives screen readers context.
          -->
          <th
            v-if="useTrailCell"
            class="table-trail-header"
            :aria-label="hasExpander ? 'Row details' : 'Row actions'"
            data-part="table-trail-header"
          >
            <span aria-hidden="true"></span>
          </th>
          <th
            v-else-if="hasActions"
            class="table-actions-header"
            :aria-label="collapseActions ? 'Row actions' : 'Actions'"
            data-part="table-actions-header"
          >
            <span aria-hidden="true"></span>
          </th>
        </tr>
      </thead>
      <!--
        One body for both layouts. Tree rows (`bodyRows` from the
        expanded tree walk) differ only in the label column, which
        wraps its cell in an indent + caret; sort and "+ N more" are
        off in tree mode upstream.
      -->
      <tbody>
        <template v-for="(entry, i) in bodyRows" :key="entry.originalIndex">
        <tr
          :class="{
            clickable: !!opts?.id,
            'is-stripe': i % 2 === 0,
            'is-expanded': expandedRows.has(entry.originalIndex),
          }"
          :aria-level="isTree ? (entry.depth ?? 0) + 1 : undefined"
          @click="onRowClick(i)"
        >
          <td
            v-for="col in visibleColumns"
            :key="col.key"
            :class="{
              num: col.align === 'right',
              'tree-label': isTree && col.key === treeCfg?.label,
              wrap: col.wrap,
            }"
            :style="{ textAlign: col.align }"
          >
            <span
              v-if="isTree && col.key === treeCfg?.label"
              class="tree-row-inner"
              :style="{ '--depth': entry.depth ?? 0 }"
            >
              <button
                v-if="entry.hasChildren && entry.nodeId"
                type="button"
                class="tree-caret"
                data-part="table-tree-caret"
                :aria-expanded="expanded.has(entry.nodeId)"
                :aria-label="expanded.has(entry.nodeId) ? 'Collapse' : 'Expand'"
                @click.stop="toggleExpansion(entry.nodeId)"
                @keydown="onCaretKey($event, entry.nodeId)"
              >
                {{ expanded.has(entry.nodeId) ? "▾" : "▸" }}
              </button>
              <!--
                Spacer keeps childless rows' labels aligned with
                their siblings that *do* have a caret. Without it,
                leaves sit to the left of expandable nodes at the
                same depth, which reads as a depth jump.
              -->
              <span v-else class="tree-caret-spacer" aria-hidden="true"></span>
              <span class="tree-label-text">{{ cell(entry.row[col.key], col.format) }}</span>
            </span>
            <span v-else-if="col.wrap" class="cell-text">{{ cell(entry.row[col.key], col.format) }}</span>
            <template v-else>{{ cell(entry.row[col.key], col.format) }}</template>
          </td>
          <!--
            Narrow trailing cell: the chevron when columns collapsed
            into an expander (the row's actions live inside it), else
            the `ellipsis` action-sheet trigger. @click.stop keeps the
            row's own click handler (and a tree caret) from also firing.
          -->
          <td v-if="useTrailCell" class="table-trail">
            <template v-if="hasExpander">
              <button
                v-if="rowHasDetails(entry.originalIndex, entry.row)"
                type="button"
                class="table-expander-btn"
                :class="{ 'is-open': expandedRows.has(entry.originalIndex) }"
                :aria-expanded="expandedRows.has(entry.originalIndex)"
                :aria-label="expandedRows.has(entry.originalIndex) ? 'Hide details' : 'Show details'"
                data-part="table-expander"
                @click.stop="toggleRowExpansion(entry.originalIndex)"
              >
                <LucentIcon name="chevron-down" :size="18" />
              </button>
            </template>
            <button
              v-else-if="visibleActionsFor(entry.originalIndex).length > 0"
              type="button"
              class="table-action-overflow-btn"
              aria-label="Row actions"
              aria-haspopup="dialog"
              data-part="table-action-overflow"
              @click.stop="openActionSheet(entry.originalIndex, $event)"
            >
              <LucentIcon name="ellipsis" :size="20" />
            </button>
          </td>
          <!--
            Actions cell (wide tables). Sticky-right keeps the buttons
            visible when data columns scroll horizontally. On touch
            screens with several actions they collapse into the
            `ellipsis` sheet trigger.
          -->
          <td
            v-else-if="hasActions"
            class="table-actions"
            :class="{ 'table-actions--mobile': collapseActions }"
          >
            <template v-if="!collapseActions">
              <button
                v-for="vis in visibleActionsFor(entry.originalIndex)"
                :key="vis.id"
                type="button"
                class="table-action-btn"
                :class="{ 'is-disabled': vis.state === 'disabled' }"
                :disabled="vis.state === 'disabled'"
                :aria-label="tooltipFor(vis)"
                :title="tooltipFor(vis)"
                data-part="table-action"
                @click.stop="onActionClick(vis.id, entry.originalIndex)"
              >
                <LucentIcon :name="getIcon(vis.id)" :size="16" />
              </button>
            </template>
            <template v-else>
              <button
                v-if="visibleActionsFor(entry.originalIndex).length > 0"
                type="button"
                class="table-action-overflow-btn"
                aria-label="Row actions"
                aria-haspopup="dialog"
                data-part="table-action-overflow"
                @click.stop="openActionSheet(entry.originalIndex, $event)"
              >
                <LucentIcon name="ellipsis" :size="20" />
              </button>
            </template>
          </td>
        </tr>
        <!--
          Expander row — rendered as a sibling <tr> so it inherits
          the row-border cadence and doesn't fight the parent row's
          layout. `colspan` spans every painted column so the `<dl>`
          fills the row edge-to-edge.
        -->
        <tr
          v-if="hasExpander && expandedRows.has(entry.originalIndex)"
          class="table-expander-row"
        >
          <td :colspan="totalColspan">
            <dl class="table-expander-list">
              <template v-for="col in collapsedColumns" :key="col.key">
                <dt>{{ col.header }}</dt>
                <dd :class="{ num: col.align === 'right' }">
                  {{ cell(entry.row[col.key], col.format) }}
                </dd>
              </template>
            </dl>
            <div
              v-if="hasActions && visibleActionsFor(entry.originalIndex).length > 0"
              class="table-expander-actions"
            >
              <button
                v-for="vis in visibleActionsFor(entry.originalIndex)"
                :key="vis.id"
                type="button"
                class="table-action-sheet-row"
                :class="{ 'is-disabled': vis.state === 'disabled' }"
                :disabled="vis.state === 'disabled'"
                data-part="table-expander-action"
                @click.stop="onActionClick(vis.id, entry.originalIndex)"
              >
                <LucentIcon :name="getIcon(vis.id)" :size="20" class="table-action-sheet-icon" />
                <span class="table-action-sheet-text">
                  <span class="table-action-sheet-label">{{ labelFor(vis.id) }}</span>
                  <span
                    v-if="vis.state === 'disabled' && vis.reason"
                    class="table-action-sheet-reason"
                  >{{ vis.reason }}</span>
                </span>
              </button>
            </div>
          </td>
        </tr>
        </template>
      </tbody>
    </table>
    <!--
      "+ N more" reveal. Outside the <table> so it spans the visible
      width of the card rather than the full scroll width — inside a
      colspan cell its centred label sat in the middle of the whole
      table, off-screen on a phone. Sticky-left keeps it in view while
      the table scrolls sideways.
    -->
    <div v-if="overflow > 0 && !isTree" class="table-overflow">
      <button
        type="button"
        class="overflow-reveal"
        data-part="table-overflow-reveal"
        :aria-label="`Show ${Math.min(overflow, maxRows)} more rows of ${overflow} remaining`"
        @click="onRevealMore"
        @keydown="onRevealMoreKey"
      >
        + {{ overflow }} more
      </button>
    </div>
    </template>

    <!--
      Action sheet. A native <dialog> inside the component (not
      teleported) so its scoped styles apply; `showModal()` puts it in
      the top layer above `.table-wrap`'s overflow, host-page
      transforms, and any enclosing drilldown modal. Never rendered in
      print.
    -->
    <dialog
      v-if="hasActions && !isPrint()"
      ref="sheetRef"
      class="table-action-sheet"
      :aria-labelledby="sheetTitleId"
      data-part="table-action-sheet"
      @close="onSheetClose"
      @click.stop="onSheetClick"
    >
      <div class="table-action-sheet-body">
        <p :id="sheetTitleId" class="table-action-sheet-title">{{ sheetRowLabel }}</p>
        <button
          v-for="vis in sheetActions"
          :key="vis.id"
          type="button"
          class="table-action-sheet-row"
          :class="{ 'is-disabled': vis.state === 'disabled' }"
          :disabled="vis.state === 'disabled'"
          data-part="table-action-sheet-row"
          @click="onSheetAction(vis.id, vis.state === 'disabled')"
        >
          <LucentIcon
            :name="getIcon(vis.id)"
            :size="20"
            class="table-action-sheet-icon"
          />
          <span class="table-action-sheet-text">
            <span class="table-action-sheet-label">{{ labelFor(vis.id) }}</span>
            <span
              v-if="vis.state === 'disabled' && vis.reason"
              class="table-action-sheet-reason"
            >{{ vis.reason }}</span>
          </span>
        </button>
      </div>
    </dialog>
  </div>
</template>

<style scoped>
/* Horizontal scroll affordance. JS (`updateScrollShadows`) toggles
   `can-scroll-left` / `can-scroll-right` from the scroller's real
   geometry, so an edge shadow only appears when there's genuinely
   offstage content on that side. The shadows are inset box-shadows:
   inset shadows paint against the element's border box (the scroll
   *viewport*), so they stay pinned to the visible edges instead of
   scrolling away with the content. Two CSS vars hold each side's
   shadow so the two classes compose without needing a third
   left+right rule. */
.table-wrap {
  /* `0 0 transparent` is a paint-nothing placeholder: `box-shadow`
     rejects the keyword `none` inside a comma-separated list, so the
     off state can't be `none` here. */
  --table-shadow-left: 0 0 transparent;
  --table-shadow-right: 0 0 transparent;
  /* 0.35 black tint reads right against the light card surface;
     dark mode bumps it below. */
  --table-shadow-alpha: 0.35;
  /* Soft outward shadow on the LEFT edge of the sticky actions
     column. Always-on at a low alpha so the panel always reads as
     floating above the row content (its primary affordance — the
     column doesn't scroll with the data). `.can-scroll-left` bumps
     the alpha so when the user has actually scrolled right and rows
     are passing under the panel, the layering is unmistakable.
     `--actions-shadow-alpha` is a separate knob from the wrap's
     edge-shadow alpha — the actions panel sits inside the wrap, so
     its shadow needs to read against the row background rather than
     the card border. */
  --actions-shadow-alpha: 0.10;
  --actions-shadow: -8px 0 10px -8px rgba(0, 0, 0, var(--actions-shadow-alpha));
  overflow-x: auto;
  background: var(--card);
  border: var(--lucent-card-border, 1px solid var(--rule));
  border-radius: 10px;
  box-shadow:
    var(--table-shadow-left),
    var(--table-shadow-right),
    var(--lucent-card-shadow, 0 0 transparent);
}
.table-wrap.can-scroll-left {
  --table-shadow-left: inset 14px 0 12px -12px rgba(0, 0, 0, var(--table-shadow-alpha));
  /* Rows are currently passing under the actions panel — pump the
     panel's outward shadow so the layering reads at a glance. */
  --actions-shadow-alpha: 0.22;
}
.table-wrap.can-scroll-right {
  --table-shadow-right: inset -14px 0 12px -12px rgba(0, 0, 0, var(--table-shadow-alpha));
}
/* Dark mode reads the same 0.35 black tint weaker against the
   darker card surface — push toward 0.6 so the affordance is
   visually equivalent in either palette. */
@media (prefers-color-scheme: dark) {
  .table-wrap {
    --table-shadow-alpha: 0.6;
    /* Same logic — soft outward shadows are barely perceptible on
       dark surfaces at 0.10/0.22; bump to 0.32/0.5 for parity. */
    --actions-shadow-alpha: 0.32;
  }
  .table-wrap.can-scroll-left {
    --actions-shadow-alpha: 0.5;
  }
}
table {
  width: 100%;
  border-collapse: collapse;
  /* Themer-facing: --lucent-font-table-cell. `font` shorthand resets
     font-variant-numeric, so we re-declare tabular-nums below. */
  font: var(--lucent-font-table-cell, 400 13px/1.5 system-ui, sans-serif);
  font-variant-numeric: tabular-nums;
}
thead th {
  text-align: left;
  /* Themer-facing typography: --lucent-table-row-pad /
     --lucent-font-table-header / --lucent-tracking-table-header /
     --lucent-case-table-header. */
  padding: var(--lucent-table-row-pad, var(--_lucent-table-row-pad, 12px 14px));
  font: var(--lucent-font-table-header, 500 10px/1.3 var(--mono, ui-monospace, monospace));
  letter-spacing: var(--lucent-tracking-table-header, 0.12em);
  text-transform: var(--lucent-case-table-header, uppercase);
  /* Themer-facing colour: --lucent-table-header-fg / -bg / -rule.
     Defaults fall back to the existing --card-muted / --rule-soft /
     --rule cascade so themes that didn't opt into the table-colour
     surface still render identically. */
  color: var(--lucent-table-header-fg, var(--card-muted));
  background: var(--lucent-table-header-bg, var(--rule-soft));
  border-bottom: var(--lucent-table-header-rule, 1px solid var(--rule));
  /* No `position: sticky; top: 0` — `.table-wrap` is the scroll
     container (overflow-x), and with no height cap a header can never
     stick to the page. `maxRows` keeps tables short instead.
     nowrap so wide datasets push the table past its container and
     the horizontal-scroll affordance on `.table-wrap` engages.
     Without this, columns shrink-wrap and the affordance never
     fires even when the dataset is genuinely too wide for the card.
     `.wrap` columns (long free text) opt out — see below. */
  white-space: nowrap;
}
/* Sortable header affordance — cursor + hover/focus state mirror
   the `.overflow-reveal` button so the two interactive surfaces in
   the table read as the same control vocabulary. */
thead th.sortable {
  cursor: pointer;
  user-select: none;
  touch-action: manipulation;
}
thead th.sortable:hover,
thead th.sortable:focus-visible {
  background: var(--rule);
  color: var(--card-ink);
  outline: none;
}
thead th.sortable:focus-visible {
  box-shadow: inset 0 0 0 2px var(--lucent-focus, currentColor);
}
thead th.is-sorted {
  color: var(--card-ink);
}
/* Inline-flex layout for the th contents. The `<th>` itself stays
   as a table-cell (changing its display would break column layout);
   the inner span owns the flex arrangement. `text-align` on the th
   still positions this inline-flex container against the left or
   right edge per `.num`. */
.th-inner {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}
.sort-indicator {
  font-size: 11px;
  opacity: 0.55;
  transition: opacity 120ms ease;
}
.sort-indicator.is-asc,
.sort-indicator.is-desc {
  opacity: 1;
}
thead th.sortable:hover .sort-indicator,
thead th.sortable:focus-visible .sort-indicator {
  opacity: 0.85;
}
/* Right-aligned numeric columns need the indicator on the *left*
   of the label so the numbers themselves stay flush to the column
   edge — otherwise the indicator pushes them inward and breaks
   tabular alignment across rows. */
thead th.sortable.num .th-label {
  order: 2;
}
thead th.sortable.num .sort-indicator {
  order: 1;
}
tbody td {
  /* Themer-facing: --lucent-table-row-pad / --lucent-table-row-rule. */
  padding: var(--lucent-table-row-pad, var(--_lucent-table-row-pad, 12px 14px));
  border-bottom: var(--lucent-table-row-rule, 1px solid var(--rule-soft));
  color: var(--card-ink);
  /* See the `thead th` note — nowrap is what makes the scroll
     affordance on `.table-wrap` actually engage when a dataset is
     wider than the card. */
  white-space: nowrap;
}
/* Long free-text columns (`wrap`, explicit or inferred) wrap inside a
   40ch measure instead of stretching the table. The cap sits on an
   inner block because table cells ignore `max-width`. `break-word`,
   not `anywhere`: `anywhere` zeroes the min-content width and would
   crush the column to a sliver at 320px. */
thead th.wrap,
tbody td.wrap {
  white-space: normal;
}
.cell-text {
  display: block;
  max-width: 40ch;
  overflow-wrap: break-word;
}
/* Narrow tables: plain (non-sortable) headers may wrap on word
   boundaries rather than setting the column width on their own. */
.is-narrow thead th:not(.sortable) .th-label {
  white-space: normal;
}
/* Touch: ~44px sortable headers. */
.is-coarse thead th.sortable {
  padding-block: 15px;
}
.is-coarse .th-inner {
  align-items: center;
}
/* Zebra striping — opt-in via `--lucent-table-row-stripe`. Painted
   on the <td> rather than the <tr> so it composes with the row-
   hover fill below (row hover is also painted on <td> for the same
   reason — `tr` backgrounds in tables are unreliable across UAs).
   `.is-stripe` is set per data row rather than `:nth-child`, which
   would count expander rows and flip every stripe below an open one;
   the expander row keeps its own tint, so hover skips it too. */
tbody tr.is-stripe td {
  background: var(--lucent-table-row-stripe, transparent);
}
/* Row hover — opt-in via `--lucent-table-row-hover-bg`. Overrides
   the zebra fill on the hovered row. */
tbody tr:not(.table-expander-row):hover td {
  background: var(--lucent-table-row-hover-bg, transparent);
}
tbody tr:last-child td {
  border-bottom: none;
}
tr.clickable {
  cursor: pointer;
}
.num {
  text-align: right;
}
/* Sits after the <table>, inside the scroller. A block child takes
   the scroller's visible width (not the scroll width); sticky-left
   keeps it there while the table scrolls sideways. The wrapper is
   just a rule + container — the button owns padding so its hover
   fill spans the full width. */
.table-overflow {
  position: sticky;
  left: 0;
  width: var(--lucent-table-viewport, 100%);
  max-width: 100%;
  box-sizing: border-box;
  border-top: var(--lucent-table-row-rule, 1px solid var(--rule-soft));
}
.overflow-reveal {
  touch-action: manipulation;
  appearance: none;
  display: block;
  width: 100%;
  padding: 10px 14px;
  background: transparent;
  border: none;
  font: inherit;
  color: var(--card-muted);
  font-style: italic;
  cursor: pointer;
  text-align: center;
}
.overflow-reveal:hover,
.overflow-reveal:focus-visible {
  background: var(--rule-soft);
  color: var(--card-ink);
  outline: none;
}
.overflow-reveal:focus-visible {
  /* Inset ring so the focus indicator doesn't clip against the
     surrounding table border. */
  box-shadow: inset 0 0 0 2px var(--lucent-focus, currentColor);
}
.table-wrap.is-loading,
.table-wrap.is-empty,
.table-wrap.is-errored {
  /* Approximate the height of a default 7-row table so layout is stable. */
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--rule);
  border-radius: 10px;
}
.table-placeholder {
  padding: 24px;
  text-align: center;
  font-size: 13px;
  line-height: 1.4;
  color: var(--card-muted);
}
.table-placeholder--error {
  color: var(--color-negative);
}
/* Tree layout — only the label column needs to know about depth and
   the caret. Other columns render flat metadata cells unchanged. The
   label column wraps (it is a `wrap` column by default) so a deep
   node with a long name stays readable on a phone instead of being
   pushed off the right edge. */
tbody td.tree-label {
  /* Drop the cell's own horizontal padding on the leading edge —
     `tree-row-inner` owns the per-row `padding-left` so depth-indent
     and the cell's gutter compose cleanly without double-padding the
     caret away from the row's left edge. */
  padding-left: 0;
}
.tree-row-inner {
  /* Indent per depth, capped so a deep hierarchy can't push labels
     off a narrow card; `aria-level` on the row still announces the
     real depth. Narrow tables indent less. */
  --tree-indent: 16px;
  --tree-max-depth: 8;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  /* Match the left gutter the rest of the table uses so depth-0
     rows align with header cells in non-tree columns. The first
     value in `--lucent-table-row-pad` would be the vertical
     padding, so we hard-code 14px to mirror the default. */
  padding-left: calc(
    14px + min(var(--depth, 0), var(--tree-max-depth)) * var(--tree-indent)
  );
}
.is-narrow .tree-row-inner {
  --tree-indent: 12px;
  --tree-max-depth: 5;
}
.is-compact .tree-row-inner {
  --tree-indent: 10px;
  --tree-max-depth: 4;
}
.tree-caret {
  /* 24px hit box inside a row that's only ~20px of text tall; the
     negative block margin keeps row height unchanged. */
  --caret: 24px;
  appearance: none;
  background: transparent;
  border: none;
  padding: 0;
  margin: -5px 0;
  width: var(--caret);
  height: var(--caret);
  touch-action: manipulation;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font: inherit;
  font-size: 11px;
  line-height: 1;
  color: var(--card-muted);
  cursor: pointer;
  flex: 0 0 auto;
}
.is-coarse .tree-caret {
  --caret: 40px;
  margin: -13px -8px;
}
.tree-caret:hover,
.tree-caret:focus-visible {
  color: var(--card-ink);
  outline: none;
}
.tree-caret:focus-visible {
  box-shadow: 0 0 0 2px var(--lucent-focus, currentColor);
  border-radius: 2px;
}
.tree-caret-spacer {
  /* Same width as `.tree-caret`'s visual box so leaf rows align with
     their expandable siblings at the same depth. On touch the caret's
     larger hit box is absorbed by its negative inline margin, so the
     spacer stays 24px. */
  display: inline-block;
  width: 24px;
  flex: 0 0 auto;
}
.tree-label-text {
  min-width: 0;
  max-width: 40ch;
  overflow-wrap: break-word;
}
.table-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 24px 0;
}

/* ─── Per-row action icons ─────────────────────────────────────────
 *
 * Sticky right column. Background paints from the same surface as the
 * surrounding row so data cells don't show through during horizontal
 * scroll. The `--row-bg` cascade falls back to `--card` on rows that
 * aren't zebra-striped; the inner button stays transparent so hover
 * still composes with the row's hover-fill.
 */
.table-actions-header,
.table-actions {
  position: sticky;
  right: 0;
  text-align: right;
  /* Two-layer left-edge treatment:
       1. `inset 1px 0 0 var(--rule-soft)` — crisp hairline divider
          between the data column and the sticky panel. Same idiom as
          the .table-wrap scroll shadows but anchored on the column.
       2. `var(--actions-shadow)` — soft outward shadow set on the
          parent .table-wrap, intensifies via `.can-scroll-left` so
          the panel reads as floating above rows that pass under it
          during horizontal scroll. Stays subtle in the rest state so
          the panel always communicates "sticky" without shouting. */
  box-shadow:
    inset 1px 0 0 var(--rule-soft),
    var(--actions-shadow);
}
/* Body cells use the card colour so data rows don't bleed through
   during horizontal scroll. */
.table-actions {
  background: var(--card);
  z-index: 1;
}
/* Header cell keeps the thead tint so the header row reads as one
   continuous band — without this, the rightmost column showed the
   plain card colour as a visible "hole" at the top-right. z-index
   keeps it above body cells scrolling under it sideways. */
.table-actions-header {
  padding: var(--lucent-table-row-pad, var(--_lucent-table-row-pad, 12px 14px));
  background: var(--lucent-table-header-bg, var(--rule-soft));
  z-index: 1;
}
.table-actions {
  /* Tighter vertical padding so the icon buttons hug the row height
     instead of pushing it out. Horizontal padding stays roughly aligned
     with data cells so the column doesn't feel detached. */
  padding: 4px 8px;
  white-space: nowrap;
}
/* Zebra striping needs to paint through the sticky cell too — otherwise
   alternating rows show a darker actions column. The cascade reads the
   per-row stripe colour the same way data cells do. */
tbody tr.is-stripe td.table-actions {
  background: var(--lucent-table-row-stripe, var(--card));
}
tbody tr:not(.table-expander-row):hover td.table-actions {
  background: var(--lucent-table-row-hover-bg, var(--card));
}
.table-action-btn,
.table-action-overflow-btn {
  touch-action: manipulation;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: 0 2px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 4px;
  color: var(--card-muted);
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;
}
.table-action-btn:hover,
.table-action-overflow-btn:hover {
  background: var(--rule);
  color: var(--card-ink);
}
.table-action-btn:focus-visible,
.table-action-overflow-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--lucent-focus, currentColor);
}
.table-action-btn:disabled,
.table-action-btn.is-disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.table-action-btn:disabled:hover,
.table-action-btn.is-disabled:hover {
  background: transparent;
  color: var(--card-muted);
}
.table-actions--mobile {
  text-align: center;
}
/* A lone inline action on a touch screen: bigger, not collapsed. */
.is-coarse .table-action-btn {
  width: 40px;
  height: 40px;
}
.table-action-overflow-btn {
  /* Bigger tap target on mobile — Apple HIG 44pt minimum. */
  width: 44px;
  height: 44px;
  margin: 0;
}

/* ─── Action sheet ─────────────────────────────────────────────────
 *
 * Native <dialog> opened with `showModal()` — top layer, so nothing
 * clips it. The display rule is gated on `[open]`: scoped specificity
 * would otherwise beat the UA's `dialog:not([open]) { display: none }`
 * (same reason as InsightDrilldown). Padding lives on the inner body
 * so a click on the dialog box itself is unambiguously the backdrop.
 * Each row is 44pt tall (touch target) with a 20px icon and the
 * action label spelled out; disabled rows show their reason.
 */
.table-action-sheet {
  padding: 0;
  border: 0;
  margin: 0;
  color: var(--card-ink);
  background: var(--card);
  width: 100%;
  max-width: min(480px, 100vw);
  max-height: 80vh;
  max-height: 80dvh;
  overflow: auto;
  overscroll-behavior: contain;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.25);
}
.table-action-sheet[open] {
  display: block;
  position: fixed;
  inset: auto 0 0 0;
  margin-inline: auto;
  animation: lucent-sheet-slide 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
/* Literal colour: older Safari doesn't inherit custom properties into
   ::backdrop. */
.table-action-sheet::backdrop {
  background: rgba(0, 0, 0, 0.35);
}
.table-action-sheet-body {
  padding: 8px 8px calc(8px + env(safe-area-inset-bottom, 0px));
}
.table-action-sheet-title {
  margin: 4px 16px 8px;
  font-size: 12px;
  color: var(--card-muted);
  overflow-wrap: anywhere;
}
@keyframes lucent-sheet-slide {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .table-action-sheet[open] {
    animation: none;
  }
}
.table-action-sheet-row {
  appearance: none;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--card-ink);
  cursor: pointer;
  font: inherit;
  text-align: left;
  min-height: 44px;
}
.table-action-sheet-row:hover,
.table-action-sheet-row:focus-visible {
  background: var(--rule-soft);
  outline: none;
}
.table-action-sheet-row:focus-visible {
  box-shadow: inset 0 0 0 2px var(--lucent-focus, currentColor);
}
.table-action-sheet-row.is-disabled,
.table-action-sheet-row:disabled {
  cursor: not-allowed;
  color: var(--card-muted);
}
.table-action-sheet-row.is-disabled:hover {
  background: transparent;
}
.table-action-sheet-icon {
  flex: 0 0 auto;
  color: inherit;
}
.table-action-sheet-text {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}
.table-action-sheet-label {
  font-weight: 500;
}
.table-action-sheet-reason {
  font-size: 12px;
  color: var(--card-muted);
  margin-top: 2px;
}

/* ─── Narrow trailing cell — chevron or sheet trigger ───────────────
 *
 * One 44px sticky-right cell replaces the actions column + chevron
 * column on narrow tables. Sticky so it stays reachable even if the
 * visible columns still overflow.
 */
.table-trail-header,
.table-trail {
  position: sticky;
  right: 0;
  width: 44px;
  padding: 0 2px;
  text-align: center;
  box-shadow:
    inset 1px 0 0 var(--rule-soft),
    var(--actions-shadow);
}
.table-trail-header {
  background: var(--lucent-table-header-bg, var(--rule-soft));
  z-index: 1;
}
.table-trail {
  background: var(--card);
  z-index: 1;
  white-space: nowrap;
}
tbody tr.is-stripe td.table-trail {
  background: var(--lucent-table-row-stripe, var(--card));
}
tbody tr:not(.table-expander-row):hover td.table-trail {
  background: var(--lucent-table-row-hover-bg, var(--card));
}
.is-compact .table-trail .table-expander-btn,
.is-compact .table-trail .table-action-overflow-btn {
  width: 40px;
  height: 40px;
}
.is-coarse .table-trail .table-expander-btn,
.is-coarse .table-trail .table-action-overflow-btn {
  width: 44px;
  height: 44px;
}
.table-expander-btn {
  touch-action: manipulation;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* 44px tap target — same rationale as the mobile action overflow
     button (Apple HIG). */
  width: 44px;
  height: 44px;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 4px;
  color: var(--card-muted);
  cursor: pointer;
  transition: color 120ms ease, transform 160ms ease, background 120ms ease;
}
.table-expander-btn:hover,
.table-expander-btn:focus-visible {
  background: var(--rule);
  color: var(--card-ink);
  outline: none;
}
.table-expander-btn:focus-visible {
  box-shadow: 0 0 0 2px var(--lucent-focus, currentColor);
}
/* Rotate the icon rather than swapping glyphs — animates the state
   change and keeps the button's hit box stable through the transition. */
.table-expander-btn.is-open {
  transform: rotate(180deg);
}
/* Expander row — sits directly under its parent row and inherits the
   same border cadence. The <dl> renders as stacked pairs; `<dt>`
   is the muted field label, `<dd>` carries the value in the primary
   ink colour so the pair reads like a compact field row. Left inset
   nods to the parent row's alignment without pulling the pairs off
   the row grid entirely. */
.table-expander-row td {
  padding: 0;
  background: var(--rule-soft);
}
.table-expander-list {
  margin: 0;
  padding: 10px 14px;
  display: grid;
  /* Label column sized to its content but never more than 40% — long
     uppercase labels wrap instead of squeezing the values. Row spacing
     is owned by per-pair padding+border below, so no `row-gap` here.
     Sticky-left + the visible-width cap keep the list in view even if
     the visible columns still scroll sideways. */
  grid-template-columns: fit-content(40%) minmax(0, 1fr);
  column-gap: 12px;
  position: sticky;
  left: 0;
  max-width: var(--lucent-table-viewport, 100%);
  box-sizing: border-box;
}
.table-expander-list dt,
.table-expander-list dd {
  margin: 0;
  padding: 6px 0;
  /* Left-align every pair. `.num` (from `col.align === "right"`)
     inherits the global right-align rule on `.num`; the expander
     stack reads better as a single left-aligned column of values
     regardless of the desktop column alignment. */
  text-align: left;
}
/* Divider between successive Field: Value pairs. `:not(:first-of-type)`
   targets every dt/dd after the first pair, so the top pair sits
   flush against the row's own top padding. Painting the border on
   both dt and dd draws a continuous line across the grid row. */
.table-expander-list dt:not(:first-of-type),
.table-expander-list dd:not(:first-of-type) {
  border-top: 1px solid var(--rule-soft);
}
.table-expander-list dt {
  white-space: normal;
  overflow-wrap: break-word;
  color: var(--card-muted);
  font: var(--lucent-font-table-header, 500 10px/1.3 var(--mono, ui-monospace, monospace));
  letter-spacing: var(--lucent-tracking-table-header, 0.12em);
  text-transform: var(--lucent-case-table-header, uppercase);
  align-self: center;
}
.table-expander-list dd {
  color: var(--card-ink);
  /* Values may include newlines or long strings — allow wrapping
     inside the expander so nothing is clipped off-screen (unlike the
     nowrap table cells above the fold). */
  white-space: normal;
  overflow-wrap: anywhere;
}
.table-expander-list dd.num {
  font-variant-numeric: tabular-nums;
}
/* Very narrow: stack each label above its value. */
.is-compact .table-expander-list {
  grid-template-columns: minmax(0, 1fr);
}
.is-compact .table-expander-list dt {
  padding-bottom: 0;
}
.is-compact .table-expander-list dd {
  padding-top: 2px;
}
.is-compact .table-expander-list dd:not(:first-of-type) {
  border-top: 0;
}
/* The row's actions, spelled out, under its collapsed fields. */
.table-expander-actions {
  position: sticky;
  left: 0;
  max-width: var(--lucent-table-viewport, 100%);
  box-sizing: border-box;
  padding: 0 6px 8px;
}

/* Dark-mode tints (matching the .table-wrap dark rule above). */
@media (prefers-color-scheme: dark) {
  .table-action-sheet::backdrop {
    background: rgba(0, 0, 0, 0.55);
  }
  .table-expander-row td {
    /* Rule-soft in dark mode is already darker than the card — the
       expander needs its own tint so it doesn't disappear into the
       row above. A hair lighter than the surrounding row background
       keeps the field/value list legible without shouting. */
    background: color-mix(in srgb, var(--rule-soft) 60%, var(--card));
  }
}
</style>
